import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { TableActions } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { selectIsMobile } from '@app/state/settings/settings.selectors';
import { PermissionGroup } from '@interfaces/a-organizar/permissionGroup';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { UserService } from '@services/http/user.service';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Observable } from 'rxjs';

@Component({
  templateUrl: './group-list.component.html',
  styleUrls: ['./group-list.component.scss'],
  standalone: false,
})
export class GroupListComponent extends VaultBaseComponent implements OnInit {
  isMobile: boolean = false;
  /** Listagem em andamento: começa verdadeiro para a tabela abrir no esqueleto, não em "Nenhum item". */
  loading = true;
  /** A primeira listagem já chegou: daí em diante o recarregamento usa o loading global. */
  private groupsLoadedOnce = false;
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  permissionGroups: PermissionGroup[] = [];
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 10,
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  search: string = '';
  groupPermissions = Permissions.PermissionGroup;

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private utilsService: UtilsService,
    private router: Router,
    private userService: UserService,
    private toast: ToastHelperService,
    store: Store<AppState>
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.verifyIsMobile();
    this.breadcrumbs = [
      {
        label: this.t(this.tk.User.Administration),
        routerLink: '/',
      },
      {
        label: this.t(this.tk.General.Groups),
        routerLink: '/dashboard/groups/list',
      },
    ];

    this.getPermissionGroups();
  }

  addGroup(): void {
    this.router.navigate(['dashboard/groups/register']);
  }

  getPermissionGroups() {
    // Primeira carga: a tabela mostra o esqueleto, sem o loading global por cima.
    // Recarregamentos (busca, página, exclusão) seguem com o loading global.
    const showGlobalLoading = this.groupsLoadedOnce;
    if (showGlobalLoading) this.utilsService.setIsLoading(true);
    this.loading = true;
    this.userService
      .getPermissionsGroups(
        '',
        {
          ...this.paginationRequest,
          Page: (this.paginationRequest?.Page || 0) + 1,
        },
        this.search
      )
      .subscribe({
        next: response => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.groupsLoadedOnce = true;
          this.loading = false;
          if (response) {
            this.permissionGroups = response?.data || [];

            const { total, currentPage, pages, itemsPerPage } = response;

            this.pagination = {
              total: total as number,
              currentPage: currentPage as number,
              pages: pages as number,
              itemsPerPage: itemsPerPage as number,
            };
          }
        },
        error: error => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.groupsLoadedOnce = true;
          this.loading = false;
          console.log('getPermissionGroups error: ', error);
          this.toast.showToastError(
            this.t(this.tk.User.UserErrorLoadingPermissions),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  handleTablePagination(pagination: PaginatorState) {
    this.paginationRequest = {
      Page: pagination.page,
      PageSize: pagination.rows,
    };
    this.getPermissionGroups();
  }

  handleTableAction(tableActions: TableActions<PermissionGroup>): void {
    switch (tableActions.action) {
      case 'delete':
        tableActions.items.forEach((userGroup: PermissionGroup, iUserGroup: number) => {
          this.utilsService.setIsLoading(true);
          this.removeUserGroup(userGroup).subscribe({
            next: () => {
              this.toast.showToastSuccess(
                this.t(this.tk.User.PermissionsGroupRemovedSuccessfully),
                ''
              );
              this.utilsService.setIsLoading(false);
              if (iUserGroup === tableActions.items.length - 1) {
                this.permissionGroups = [];
                this.getPermissionGroups();
              }
            },
            error: error => {
              this.utilsService.setIsLoading(false);
              console.log('removeUser error: ', error);
              this.toast.showToastError(
                this.t(this.tk.User.UserErrorRemovingPermissionsGroup),
                error?.errors?.length
                  ? error.errors[0].message
                  : this.t(this.tk.General.RequestError)
              );
            },
          });
        });
        break;
      default:
        break;
    }
  }

  removeUserGroup(permissionGroup: PermissionGroup): Observable<PermissionGroup | null> {
    return this.userService.deleteUserGroup(permissionGroup.id);
  }

  handleSearch(search: string = ''): void {
    this.search = search;
    this.getPermissionGroups();
  }

  verifyIsMobile() {
    if (this.store) {
      this.store.select(selectIsMobile).subscribe(isMobile => (this.isMobile = isMobile));
    }
  }
}
